import { useTranslation } from "react-i18next"

import { candidateStatusLabel } from "@/components/candidates/candidate-status-badge"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import {
  CANDIDATE_STATUSES,
  type CandidateStatus,
  type CandidateStatusEvent,
} from "@/types/candidates"

/**
 * OKS-18 / OKS-26 — Status history timeline (UX Spec §Status history).
 *
 * Chronological order (oldest-on-top). BE adapter guarantees ≥1 row
 * always (synthetic creation event when no transitions exist).
 *
 * **OKS-26 refactor — pure presentation.** The component receives
 * pre-resolved labels and a `statusLabel` resolver callback. i18n
 * lives entirely in the consumer (candidate Show.tsx vs crew
 * Show.tsx) so the `inertia_i18n_health_spec` scanner can statically
 * trace every literal `t("...")` call to its consuming component.
 * Generic `t(keyVariable)` patterns are scanner-invisible and produced
 * orphan-key false-positives in BE-5; this rewrite eliminates them.
 *
 * Locked testids:
 *   - `{testIdRoot}-history` — `<ol>`
 *   - `{testIdRoot}-event` — each `<li>`
 *   - `{testIdRoot}-event-actor` — actor name span
 *   - `{testIdRoot}-event-time` — `<time>` element
 *   - `{testIdRoot}-event-reason` — reason text (when non-null)
 *
 * `testIdRoot` defaults to `candidate-detail-status` so OKS-18 callers
 * are unchanged. OKS-26 passes `creator-detail-status` per the locked
 * QA contract.
 *
 * **Optional callable** — `statusLabel(status: string): string`. Not
 * consumed by the Timeline body itself (the consumer's
 * `formatEventLabel` does the rendering); exported on the hook return
 * type so consumers that want to label a status outside the timeline
 * (e.g. an inline pill in a different surface) can reuse the same
 * resolver.
 */

/**
 * Generic event shape — wider than `CandidateStatusEvent` so the
 * crew-side caller can pass `CrewStatusEvent` without a type assertion.
 * Both narrow types remain assignable.
 */
export interface StatusHistoryEvent {
  from: string | null
  to: string
  actor: { id: number; name: string } | null
  at: string
  reason?: string | null
  /**
   * OKS-952 — the coded rejection reason, rendered as a labelled chip ABOVE
   * the free-text note.
   *
   * `null`/absent for every pre-taxonomy row and for rejections made without a
   * code (it is optional). Those rows render EXACTLY as before — the note
   * alone, or nothing. **No "unknown reason" placeholder**: the reason was
   * never asked for, and a placeholder would assert it is unknown, which is a
   * different and false claim.
   *
   * Optional, so crew-side callers that omit it keep compiling.
   */
  reason_code?: string | null
  /**
   * OKS-465 — event name. When it is a lifecycle event (`remove` /
   * `restore`, which carry `from === to`), the timeline renders the
   * lifecycle label from `lifecycleEventLabel` instead of a status
   * arrow. Optional + ignored by the crew-side caller.
   */
  event?: string | null
}

interface StatusHistoryTimelineProps {
  events: StatusHistoryEvent[] | CandidateStatusEvent[]
  /**
   * Resolve a transition to its localized label
   * (`{from} → {to}`). The synthetic creation row has `from === null`;
   * the consumer decides how to render that (e.g. "Created → Active").
   */
  formatEventLabel: (from: string | null, to: string) => string
  /**
   * OKS-465 — Resolve a lifecycle event (`remove` / `restore`) to its
   * localized label ("Removed from pipeline" / "Restored to pipeline").
   * Returns null for non-lifecycle events so the from→to transition
   * label is used instead. Optional — crew-side callers omit it.
   */
  lifecycleEventLabel?: (event: string) => string | null
  /** Localized "System"/"Système" for null-actor synthetic rows. */
  nullActorLabel: string
  /** Localized section title (`<CardTitle>` content). */
  sectionTitle: string
  /** Map from status value → Tailwind class for the dot color. */
  dotColorMap: Record<string, string>
  /**
   * When true, the null-actor name renders italic + muted (UX V2
   * lock for the crew-side timeline). Defaults to false to preserve
   * OKS-18's plain rendering.
   */
  nullActorItalic?: boolean
  /**
   * Testid root. Default: `candidate-detail-status` (preserves OKS-18
   * locked testids). OKS-26 passes `creator-detail-status` per the
   * QA contract.
   */
  testIdRoot?: string
  /**
   * OKS-952 — resolves a rejection `reason_code` to its display label.
   *
   * Injected rather than resolved inside, matching this component's existing
   * contract: it takes label FORMATTERS and calls no `t()` itself, so the
   * crew-side caller can reuse it with a different copy namespace.
   *
   * OMIT IT to suppress the chip entirely — that is how the crew timeline keeps
   * its current output while the candidate detail page opts in.
   */
  reasonCodeLabel?: (code: string) => string
}

export function StatusHistoryTimeline({
  events,
  formatEventLabel,
  lifecycleEventLabel,
  nullActorLabel,
  sectionTitle,
  dotColorMap,
  nullActorItalic = false,
  testIdRoot = "candidate-detail-status",
  reasonCodeLabel,
}: StatusHistoryTimelineProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{sectionTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol
          data-testid={`${testIdRoot}-history`}
          role="list"
          className="border-muted-foreground/30 flex flex-col gap-4 border-l pl-6"
        >
          {(events as StatusHistoryEvent[]).map((event, idx) => {
            // OKS-465 — lifecycle events (remove/restore) render a
            // dedicated label rather than a `from → to` arrow (their
            // from === to). Falls back to the transition label.
            const lifecycleLabel =
              event.event && lifecycleEventLabel
                ? lifecycleEventLabel(event.event)
                : null
            return (
              <li
                key={`${event.at}-${idx}`}
                data-testid={`${testIdRoot}-event`}
                data-state-from={event.from ?? "created"}
                data-state-to={event.to}
                data-event={event.event ?? undefined}
                className="relative"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "border-background absolute top-1 -left-[1.85rem] size-3 rounded-full border-2",
                    lifecycleLabel
                      ? "bg-zinc-400"
                      : (dotColorMap[event.to] ?? "bg-zinc-400"),
                  )}
                />
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm">
                    {lifecycleLabel ?? formatEventLabel(event.from, event.to)}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    <span
                      data-testid={`${testIdRoot}-event-actor`}
                      className={cn(
                        event.actor === null &&
                          nullActorItalic &&
                          "text-muted-foreground italic",
                      )}
                    >
                      {event.actor?.name ?? nullActorLabel}
                    </span>
                    <span aria-hidden="true"> · </span>
                    <time
                      dateTime={event.at}
                      title={new Date(event.at).toLocaleString()}
                      data-testid={`${testIdRoot}-event-time`}
                    >
                      {/*
                      Phase 4 F6 — no aria-label on `<time>`; the
                      `dateTime` attribute + the rendered date string
                      ARE the accessible value.
                    */}
                      {new Date(event.at).toLocaleDateString()}
                    </time>
                  </p>
                  {/*
                  OKS-19 F11 — render the rejection / blocklist reason
                  text below the actor line when non-null. Crew callers
                  (OKS-26) omit `reason` (V1 doesn't surface reasons on
                  the crew timeline).
                  Locked testid: `{testIdRoot}-event-reason`.
                */}
                  {/*
                  OKS-952 — the coded reason chip. Sits ABOVE the note: the
                  code is the primary datum and the note elaborates it. Rendered
                  only when a code exists, so legacy rows are untouched.
                */}
                  {event.reason_code && reasonCodeLabel && (
                    <p className="mt-1">
                      <Badge
                        variant="outline"
                        className="font-normal"
                        data-testid={`${testIdRoot}-event-reason-code`}
                        data-reason-code={event.reason_code}
                      >
                        {reasonCodeLabel(event.reason_code)}
                      </Badge>
                    </p>
                  )}
                  {event.reason !== null &&
                    event.reason !== undefined &&
                    event.reason !== "" && (
                      <p
                        data-testid={`${testIdRoot}-event-reason`}
                        className="text-muted-foreground mt-1 text-xs whitespace-pre-line italic"
                      >
                        {event.reason}
                      </p>
                    )}
                </div>
              </li>
            )
          })}
        </ol>
      </CardContent>
    </Card>
  )
}

// OKS-983 — the candidate label resolver is NOT defined here. It is imported
// from `candidate-status-badge.tsx`, which is the single source for
// `candidates.status.*` copy.
//
// This module used to carry its own same-named copy. OKS-948 added
// `pending_brand_validation` and `contacted` to the badge's switch and to
// `CANDIDATE_DOT_COLOR_MAP` below — but not to the local switch, which had a
// `default: return status` and so emitted the raw enum value instead of a
// label. The colour map was typed `Record<CandidateStatus, string>` and could
// not compile incomplete; the switch took a bare `string` and could. The
// duplicate is deleted rather than patched so there is no second copy to drift.
//
// The badge's switch is exhaustive over `CandidateStatus` with **no `default`**,
// so status #10 is a compile error there rather than a silent raw key here.

/**
 * Default candidate-side dot-color palette. Re-exported so the page
 * doesn't have to duplicate the map.
 */
export const CANDIDATE_DOT_COLOR_MAP: Record<CandidateStatus, string> = {
  new: "bg-blue-400",
  reviewed: "bg-amber-400",
  // OKS-948 — mirrors `CandidateStatusBadge`'s families: orange for the
  // brand-validation wait (next to Reviewed's amber), teal for Contacted.
  pending_brand_validation: "bg-orange-400",
  approved: "bg-emerald-400",
  contacted: "bg-teal-400",
  invited: "bg-violet-400",
  joined: "bg-emerald-600",
  rejected: "bg-red-400",
  blocklisted: "bg-zinc-500",
}

/**
 * OKS-983 — narrow before calling the badge resolver.
 *
 * `StatusHistoryEvent.to` is deliberately `string` (the crew caller passes a
 * different enum through the same component), while `candidateStatusLabel`
 * takes `CandidateStatus` and has no `default` arm. Asserting the wider type
 * onto the narrower one would compile and then return `undefined` for any
 * out-of-enum string, which React renders as nothing — the label silently loses
 * a side. That is worse than the raw key this ticket fixes, because it is
 * invisible. Narrowing keeps the compile-time guarantee and leaves an
 * unrecognised value VISIBLE as its raw string.
 */
function isCandidateStatus(value: string): value is CandidateStatus {
  return (CANDIDATE_STATUSES as readonly string[]).includes(value)
}

/**
 * Default candidate-side props for the timeline. Encapsulates the
 * literal `t()` calls so the inertia_i18n scanner sees every key.
 * Consumers spread the result onto `<StatusHistoryTimeline>`.
 *
 * Usage (in candidate Show.tsx):
 *   const timelineProps = useCandidateStatusHistoryProps()
 *   <StatusHistoryTimeline events={status_history} {...timelineProps} />
 */
export function useCandidateStatusHistoryProps(): Pick<
  StatusHistoryTimelineProps,
  | "formatEventLabel"
  | "lifecycleEventLabel"
  | "nullActorLabel"
  | "sectionTitle"
  | "dotColorMap"
  | "reasonCodeLabel"
> {
  const { t } = useTranslation()
  // OKS-983 — one resolver for both sides of the arrow. The bug this replaces
  // was reachable from the `from` side as well as the `to` side.
  const statusLabel = (value: string) =>
    isCandidateStatus(value) ? candidateStatusLabel(t, value) : value
  return {
    formatEventLabel: (from, to) => {
      const fromLabel =
        from === null
          ? t("candidates.detail.status_history.created_event_from")
          : statusLabel(from)
      return t("candidates.detail.status_history.event_label", {
        from: fromLabel,
        to: statusLabel(to),
      })
    },
    // OKS-465 — lifecycle event labels (remove/restore). Literal `t()`
    // calls in a switch keep the inertia_i18n scanner happy.
    lifecycleEventLabel: (event) => {
      switch (event) {
        case "remove":
          return t("candidates.detail.status_history.removed_event")
        case "restore":
          return t("candidates.detail.status_history.restored_event")
        default:
          return null
      }
    },
    nullActorLabel: t("candidates.detail.status_history.actor_unknown"),
    sectionTitle: t("candidates.detail.status_history.section_title"),
    dotColorMap: CANDIDATE_DOT_COLOR_MAP,
    // OKS-952 — the same `candidates.reason_code.*` namespace the picker and
    // the list badge use, so one code has one label across the product. Only
    // the candidate timeline opts in; the crew caller omits it and its output
    // is unchanged.
    reasonCodeLabel: (code) => t(`candidates.reason_code.${code}`),
  }
}
